import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, FileText, Calendar, Sparkles } from 'lucide-react';

const PromoBanners = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const banners = [
    {
      id: 1,
      icon: Calendar,
      titleAr: 'احجز استشارتك',
      titleEn: 'Book Consultation',
      descriptionAr: 'احجز موعدًا مع محامٍ متخصص',
      descriptionEn: 'Book an appointment with a specialist',
      link: '/book-consultation',
      bgGradient: 'from-wazin-navy via-wazin-navy-600 to-wazin-navy-700',
      iconColor: 'text-wazin-gold'
    },
    {
      id: 2,
      icon: FileText,
      titleAr: 'العقود الرقمية',
      titleEn: 'Digital Contracts',
      descriptionAr: 'صياغة ومراجعة عقودك إلكترونيًا',
      descriptionEn: 'Draft and review contracts digitally',
      link: '/services/contracts',
      bgGradient: 'from-wazin-gold via-wazin-gold-500 to-wazin-gold-600',
      iconColor: 'text-wazin-navy'
    },
    {
      id: 3,
      icon: Sparkles,
      titleAr: 'خدمات متخصصة',
      titleEn: 'Specialized Services',
      descriptionAr: 'ملكية فكرية، امتثال قانوني، وتأسيس الشركات',
      descriptionEn: 'Intellectual property, legal compliance, and company formation',
      link: '/services/all',
      bgGradient: 'from-wazin-navy via-wazin-navy-600 to-wazin-navy-700',
      iconColor: 'text-wazin-gold'
    },
    {
      id: 4,
      icon: Phone,
      titleAr: 'تواصل معنا',
      titleEn: 'Contact Us',
      descriptionAr: 'تواصل معنا مباشرة عبر الهاتف أو الواتساب',
      descriptionEn: 'Contact us directly via phone or WhatsApp',
      link: '/#locations',
      bgGradient: 'from-wazin-gold via-wazin-gold-500 to-wazin-gold-600',
      iconColor: 'text-wazin-navy'
    }
  ];

  return (
    <div className="bg-gray-50 py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-14">
          <h2 className="text-4xl font-bold text-wazin-navy mb-4">
            {i18n.language === 'ar' ? 'خدماتنا المميزة' : 'Featured Services'}
          </h2>
          <p className="text-xl text-gray-600">
            {i18n.language === 'ar' ? 'اكتشف ما نقدمه لك' : 'Discover what we offer'}
          </p>
        </div>

        {/* Perfectly Aligned Grid with Fixed Heights */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {banners.map((banner) => {
            const Icon = banner.icon;
            return (
              <div
                key={banner.id}
                onClick={() => navigate(banner.link)}
                className={`bg-gradient-to-br ${banner.bgGradient} rounded-2xl p-8 cursor-pointer 
                  transform hover:scale-105 transition-all duration-250 shadow-xl hover:shadow-2xl
                  group relative overflow-hidden h-[320px] flex flex-col justify-between`}
              >
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-grid-pattern"></div>
                </div>

                {/* Content */}
                <div className="relative z-10" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  {/* Icon - Larger */}
                  <div className="mb-6">
                    <div className="bg-white/25 backdrop-blur-sm w-18 h-18 rounded-xl flex items-center justify-center
                      group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg">
                      <Icon className={`w-9 h-9 ${banner.iconColor}`} />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-white mb-4 leading-tight">
                    {i18n.language === 'ar' ? banner.titleAr : banner.titleEn}
                  </h3>

                  {/* Description */}
                  <p className="text-base text-white/95 leading-relaxed mb-6">
                    {i18n.language === 'ar' ? banner.descriptionAr : banner.descriptionEn}
                  </p>
                </div>

                {/* Arrow Indicator - Bottom Aligned */}
                <div className="relative z-10 flex items-center" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                  <span className="text-white font-semibold text-base group-hover:text-white transition-colors">
                    {i18n.language === 'ar' ? 'اكتشف المزيد' : 'Learn More'}
                  </span>
                  <svg
                    className={`w-5 h-5 text-white/90 group-hover:text-white transition-all 
                      ${i18n.language === 'ar' ? 'mr-2 group-hover:-translate-x-2' : 'ml-2 group-hover:translate-x-2'}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d={i18n.language === 'ar' ? 'M15 19l-7-7 7-7' : 'M9 5l7 7-7 7'}
                    />
                  </svg>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CSS for Grid Pattern */}
      <style>{`
        .bg-grid-pattern {
          background-image:
            linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 20px 20px;
        }
      `}</style>
    </div>
  );
};

export default PromoBanners;
